import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { Card } from './Card';

export interface StatTileTrend {
  value: number;
  direction: 'up' | 'down';
}

interface StatTileProps {
  label: string;
  value: string | number;
  icon: ReactNode;
  /** Tailwind background class for the icon chip, e.g. 'bg-brand-500' */
  iconClassName?: string;
  trend?: StatTileTrend;
  /** If provided, the whole tile links here and reveals a chevron on hover */
  href?: string;
}

const TileContent: React.FC<Pick<StatTileProps, 'label' | 'value' | 'icon' | 'iconClassName' | 'trend'> & { hoverable?: boolean }> = ({
  label,
  value,
  icon,
  iconClassName,
  trend,
  hoverable,
}) => (
  <div className="flex items-center gap-4">
    <div className={clsx('flex-shrink-0 p-3 rounded-lg', iconClassName ?? 'bg-brand-500')}>
      <div className="h-6 w-6 text-white">{icon}</div>
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">{label}</p>
      <div className="flex items-baseline gap-2">
        <p className="text-2xl font-semibold text-gray-900 dark:text-white">{value}</p>
        {trend && (
          <span
            className={clsx(
              'text-xs font-medium',
              trend.direction === 'up' ? 'text-green-600 dark:text-green-400' : 'text-danger-600 dark:text-danger-400'
            )}
          >
            {trend.direction === 'up' ? '▲' : '▼'} {Math.abs(trend.value)}%
          </span>
        )}
      </div>
    </div>
    {hoverable && (
      <ChevronRightIcon className="h-4 w-4 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
    )}
  </div>
);

export const StatTile: React.FC<StatTileProps> = ({ href, ...props }) => {
  if (href) {
    return (
      <Link to={href} className="group block">
        <Card className="transition-shadow hover:shadow-md">
          <TileContent {...props} hoverable />
        </Card>
      </Link>
    );
  }

  return (
    <Card>
      <TileContent {...props} />
    </Card>
  );
};
